/**
 * TableDataRow — the interactive data-row subsystem (row interaction attributes,
 * click/keydown handling, expand button + expanded row). Split out of
 * FmTable.render so neither module exceeds the size gate. Pure DS internals;
 * the FmTable public API and rendering behavior are unchanged.
 */
import React from 'react';
import type {
  FmTableColumn,
  FmTableProps,
  FmTableRowDataAttributes,
  FmTableRowAttributes,
} from '../../types/fm-table';
import {
  cx,
  expansionContentId,
  type FmTableInternalRow,
} from './FmTable.utils';
import type { FmTableA11yLabels } from './FmTable.render';

type RowAttrs = FmTableRowAttributes;

const INTERACTIVE_DESCENDANT_SELECTOR = [
  'button',
  'a[href]',
  'input',
  'select',
  'textarea',
  '[role="button"]',
  '[role="link"]',
  '[role="checkbox"]',
  '[role="menuitem"]',
  '[contenteditable="true"]',
  '[data-fm-table-row-toggle-ignore]',
].join(',');

function shouldIgnoreRowClickExpansion(event: React.MouseEvent<HTMLTableRowElement>): boolean {
  const target = event.target;
  if (!(target instanceof Element) || target === event.currentTarget) return false;
  return Boolean(target.closest(INTERACTIVE_DESCENDANT_SELECTOR));
}

export interface DataRowProps<T> {
  record: Extract<FmTableInternalRow<T>, { kind: 'data' }>;
  rowProps?: FmTableProps<T>['rowProps'];
  rowClassName?: FmTableProps<T>['rowClassName'];
  rowTestId?: FmTableProps<T>['rowTestId'];
  onRowSelect?: FmTableProps<T>['onRowSelect'];
  onRowDoubleClick?: FmTableProps<T>['onRowDoubleClick'];
  rowAriaLabel?: FmTableProps<T>['rowAriaLabel'];
  rowCurrent?: FmTableProps<T>['rowCurrent'];
  rowDataAttributes?: FmTableProps<T>['rowDataAttributes'];
  expandable?: FmTableProps<T>['expandable'];
  expandedKeys: Set<string>;
  tableId: string;
  totalColumnCount: number;
  showExpandColumn: boolean;
  columns: FmTableColumn<T>[];
  rowKey: (row: T) => string;
  a11yLabels: FmTableA11yLabels;
  onToggleRow: (row: T) => void;
  renderCell: (row: T, col: FmTableColumn<T>) => React.ReactNode;
}

function computeRowState<T extends object>(
  record: Extract<FmTableInternalRow<T>, { kind: 'data' }>,
  expandable: FmTableProps<T>['expandable'] | undefined,
  expandedKeys: Set<string>,
  tableId: string,
): {
  rowCanExpandByClick: boolean;
  expanded: boolean;
  expandedContentId: string;
} {
  const expandableRow = expandable?.rowExpandable?.(record.row) ?? true;
  const rowCanExpandByClick = Boolean(expandable?.expandRowByClick && expandableRow);
  const expanded = Boolean(expandable && expandedKeys.has(record.key) && expandableRow);
  const expandedContentId = expansionContentId(tableId, record.key);
  return { rowCanExpandByClick, expanded, expandedContentId };
}

function isRowInteractive(
  onClick: React.MouseEventHandler<HTMLTableRowElement> | undefined,
  rowCanExpandByClick: boolean,
  rowCanSelect: boolean,
): boolean {
  return Boolean(onClick || rowCanExpandByClick || rowCanSelect);
}

function dataRowClassName<T>(
  row: T,
  interactive: boolean,
  rowClassName: FmTableProps<T>['rowClassName'],
  callerClassName: string | undefined,
): string {
  return cx(
    'fm-table__row',
    'fm-table__data-row',
    interactive && 'fm-table__row--interactive',
    rowClassName?.(row),
    callerClassName,
  );
}

export function TableDataRow<T extends object>({
  record,
  rowProps,
  rowClassName,
  rowTestId,
  onRowSelect,
  onRowDoubleClick,
  rowAriaLabel,
  rowCurrent,
  rowDataAttributes,
  expandable,
  expandedKeys,
  tableId,
  totalColumnCount,
  showExpandColumn,
  columns,
  rowKey,
  a11yLabels,
  onToggleRow,
  renderCell,
}: DataRowProps<T>): React.ReactElement {
  const attrs = (rowProps?.(record.row) ?? {}) as RowAttrs;
  const { className: callerClassName, onClick, onKeyDown, tabIndex, 'data-testid': callerTestId, ...restAttrs } = attrs;
  const semanticAttrs = (rowDataAttributes?.(record.row) ?? {}) as FmTableRowDataAttributes;
  const { rowCanExpandByClick, expanded, expandedContentId } = computeRowState(
    record,
    expandable,
    expandedKeys,
    tableId,
  );
  const testId = callerTestId ?? rowTestId?.(record.row);
  const rowCanSelect = Boolean(onRowSelect);
  const interactive = isRowInteractive(onClick, rowCanExpandByClick, rowCanSelect);

  return (
    <React.Fragment key={record.key}>
      <tr
        {...restAttrs}
        {...semanticAttrs}
        {...rowInteractionAttributes({
          rowCanExpandByClick,
          rowCanSelect,
          expanded,
          expandedContentId,
          tabIndex,
          ariaLabel: rowAriaLabel?.(record.row),
          ariaCurrent: rowCurrent?.(record.row),
        })}
        className={dataRowClassName(record.row, interactive, rowClassName, callerClassName)}
        data-testid={testId}
        onClick={(event) => handleRowClick(event, record.row, rowCanExpandByClick, { callerOnClick: onClick, onRowSelect, onToggleRow })}
        onDoubleClick={onRowDoubleClick ? () => onRowDoubleClick(record.row) : undefined}
        onKeyDown={rowKeyDownHandler(record.row, rowCanExpandByClick, onKeyDown, onRowSelect, onToggleRow)}
      >
        {showExpandColumn ? (
          <td className="fm-table__cell fm-table__expand-cell" data-label="">
            {renderExpandButton({ row: record.row, rowKey, expandable, expandedKeys, tableId, a11yLabels, onToggleRow })}
          </td>
        ) : null}
        {columns.map((col) => renderCell(record.row, col))}
      </tr>
      {expanded ? renderExpandedRow(record.row, expandable, expandedContentId, totalColumnCount) : null}
    </React.Fragment>
  );
}

function rowInteractionAttributes(args: {
  rowCanExpandByClick: boolean;
  rowCanSelect: boolean;
  expanded: boolean;
  expandedContentId: string;
  tabIndex: number | undefined;
  ariaLabel?: string;
  ariaCurrent?: boolean | 'page' | 'step' | 'location' | 'date' | 'time';
}): React.HTMLAttributes<HTMLTableRowElement> {
  const { rowCanExpandByClick, rowCanSelect, expanded, expandedContentId, tabIndex, ariaLabel, ariaCurrent } = args;
  const interactive = rowCanExpandByClick || rowCanSelect;
  const resolvedAriaCurrent: React.AriaAttributes['aria-current'] =
    ariaCurrent === true ? 'true' : ariaCurrent || undefined;
  if (!rowCanExpandByClick) {
    return {
      tabIndex: interactive ? tabIndex ?? 0 : tabIndex,
      'aria-label': ariaLabel,
      'aria-current': resolvedAriaCurrent,
    };
  }
  return {
    tabIndex: tabIndex ?? 0,
    'aria-expanded': expanded,
    'aria-controls': expanded ? expandedContentId : undefined,
    'aria-label': ariaLabel,
    'aria-current': resolvedAriaCurrent,
  };
}

interface RowClickHandlers<T> {
  callerOnClick: React.MouseEventHandler<HTMLTableRowElement> | undefined;
  onRowSelect: ((row: T) => void) | undefined;
  onToggleRow: (row: T) => void;
}

function handleRowClick<T>(
  event: React.MouseEvent<HTMLTableRowElement>,
  row: T,
  rowCanExpandByClick: boolean,
  handlers: RowClickHandlers<T>,
): void {
  const { callerOnClick, onRowSelect, onToggleRow } = handlers;
  callerOnClick?.(event);
  if (event.defaultPrevented) return;
  if (shouldIgnoreRowClickExpansion(event)) return;
  onRowSelect?.(row);
  if (event.defaultPrevented || !rowCanExpandByClick) return;
  onToggleRow(row);
}

function rowKeyDownHandler<T>(
  row: T,
  rowCanExpandByClick: boolean,
  callerOnKeyDown: React.KeyboardEventHandler<HTMLTableRowElement> | undefined,
  onRowSelect: ((row: T) => void) | undefined,
  onToggleRow: (row: T) => void,
): React.KeyboardEventHandler<HTMLTableRowElement> | undefined {
  const rowCanSelect = Boolean(onRowSelect);
  if (!callerOnKeyDown && !rowCanExpandByClick && !rowCanSelect) return undefined;
  return (event) => {
    callerOnKeyDown?.(event);
    if (event.defaultPrevented) return;
    if (event.target !== event.currentTarget) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onRowSelect?.(row);
    if (!rowCanExpandByClick) return;
    onToggleRow(row);
  };
}

function renderExpandButton<T>(options: {
  row: T;
  rowKey: (row: T) => string;
  expandable: FmTableProps<T>['expandable'];
  expandedKeys: Set<string>;
  tableId: string;
  a11yLabels: FmTableA11yLabels;
  onToggleRow: (row: T) => void;
}): React.ReactNode {
  const { row, rowKey, expandable, expandedKeys, tableId, a11yLabels, onToggleRow } = options;
  if (!expandable) return null;
  const key = rowKey(row);
  if (!(expandable.rowExpandable?.(row) ?? true)) return null;
  const expanded = expandedKeys.has(key);
  return (
    <button
      type="button"
      className={cx('fm-table__expand-button', expanded && 'fm-table__expand-button--expanded')}
      data-testid={expandable.expandButtonTestId?.(row)}
      aria-expanded={expanded}
      aria-controls={expanded ? expansionContentId(tableId, key) : undefined}
      aria-label={expanded ? a11yLabels.collapseRow : a11yLabels.expandRow}
      onClick={(event) => {
        event.stopPropagation();
        onToggleRow(row);
      }}
    >
      <span className="fm-table__expand-icon" aria-hidden="true">{'\u203A'}</span>
    </button>
  );
}

function renderExpandedRow<T>(
  row: T,
  expandable: FmTableProps<T>['expandable'],
  expandedContentId: string,
  totalColumnCount: number,
): React.ReactNode {
  return (
    <tr className="fm-table__expanded-row">
      <td className="fm-table__expanded-cell" colSpan={totalColumnCount}>
        <div id={expandedContentId} className="fm-table__expanded-content" data-testid="fm-table-expanded-content">
          {expandable?.expandedRowRender(row)}
        </div>
      </td>
    </tr>
  );
}
